import api from './api';

// Base CRUD service patterns
export interface BaseEntity {
  id: number;
}

// Roles
export interface Role extends BaseEntity {
  name: string;
}

// Permissions
export interface Permission extends BaseEntity {
  name: string;
}

// Users
export interface User extends BaseEntity {
  username: string;
  motdepasse?: string;
  role_id?: number;
  email?: string;
  role?: Role;
}

// Type Article
export interface TypeArticle extends BaseEntity {
  description: string;
}

// Fournisseurs
export interface Fournisseur extends BaseEntity {
  nom_complet: string;
  numero_mobile?: string;
  notes?: string;
  credit_depart?: number;
}

// Caisses
export interface Caisse extends BaseEntity {
  nom_caisse: string;
  montant_depart?: number;
  devise?: string;
}

// Clients
export type ClientType = 'Particulier' | 'Entreprise';
export interface Client extends BaseEntity {
  type_client: ClientType;
  nom_complet?: string;
  numero_passeport?: string;
  numero_mobile?: string;
  numero_mobile_2?: string;
  email?: string;
  date_naissance?: string;
  notes?: string;
  image?: string;
  nom_entreprise?: string;
  rc?: string;
  nif?: string;
  ai?: string;
  nis?: string;
  prefere_facturation?: boolean;
}

// Articles
export interface Article extends BaseEntity {
  date_depart?: string;
  label: string;
  description?: string;
  fournisseur_id?: number;
  commission?: number;
  offre_limitee?: boolean;
  disponibilite?: number;
  prix_offre?: number;
  is_archiver?: boolean;
  id_type_article?: number;
  fournisseur?: Fournisseur;
  type_article?: TypeArticle;
}

// Commandes
export interface Commande extends BaseEntity {
  client_id?: number;
  article_id?: number;
  date?: string;
  beneficiaire?: boolean;
  nom?: string;
  prenom?: string;
  date_naissance?: string;
  genre?: string;
  numero_passport?: string;
  date_expiration_passport?: string;
  numero_mobile?: string;
  remarques?: string;
  image?: string;
  prix?: number;
  reductions?: number;
  autre_reductions?: number;
  taxes?: number;
  client?: Client;
  article?: Article;
}

// Reductions
export interface Reduction extends BaseEntity {
  type_article_id?: number;
  reference?: string;
  reduction_fixe?: boolean;
  montant_reduction_fixe?: number;
  reduction_pourcentage?: number;
  type_article?: TypeArticle;
}

// Taxes
export interface Taxe extends BaseEntity {
  id_type_article?: number;
  reference?: string;
  taxe_fixe?: boolean;
  montant_taxe_fixe?: number;
  taxe_pourcentage?: number;
  type_article?: TypeArticle;
}

// General Info
export interface GeneralInfo extends BaseEntity {
  logo?: string | null;
  phone?: string;
  emailcontact?: string;
  emailadhesion?: string;
  emailtraining?: string;
  address?: string;
  rib?: string;
}


// Generic CRUD service
class AdminService<T extends BaseEntity> {
  constructor(private endpoint: string) { }

  async getAll(): Promise<T[]> {
    try {
      const response = await api.get(`/${this.endpoint}`);
      console.log(`API Response for ${this.endpoint}:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching ${this.endpoint}:`, error);
      throw error;
    }
  }

  async getById(id: number): Promise<T> {
    const response = await api.get(`/${this.endpoint}/${id}`);
    return response.data;
  }

  async create(data: Partial<T>): Promise<T> {
    const response = await api.post(`/${this.endpoint}`, data);
    return response.data;
  }

  async update(id: number, data: Partial<T>): Promise<T> {
    const response = await api.put(`/${this.endpoint}/${id}`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/${this.endpoint}/${id}`);
  }
}

// Export service instances
export const rolesService = new AdminService<Role>('admin/roles');
export const permissionsService = new AdminService<Permission>('admin/permissions');
export const usersService = new AdminService<User>('admin/users');
export const typeArticleService = new AdminService<TypeArticle>('admin/type-article');
export const fournisseursService = new AdminService<Fournisseur>('admin/fournisseurs');
export const caissesService = new AdminService<Caisse>('admin/caisses');
export const clientsService = new AdminService<Client>('admin/clients');
export const articlesService = new AdminService<Article>('admin/articles');
export const commandesService = new AdminService<Commande>('admin/commandes');
export const reductionsService = new AdminService<Reduction>('admin/reductions');
export const taxesService = new AdminService<Taxe>('admin/taxes');
export const infoAgenceService = new AdminService<any>('admin/info-agence'); // Kept for backward compatibility if needed, though likely unused now.

export const adminService = {
  getGeneralInfo: async (): Promise<GeneralInfo> => {
    const response = await api.get('/general-info');
    return response.data;
  },
  updateGeneralInfo: async (data: Partial<GeneralInfo>): Promise<GeneralInfo> => {
    const response = await api.put('/general-info', data);
    return response.data;
  }
};

// Logo upload service
export const logoUploadService = {
  async uploadLogo(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post<{ url: string; filename: string }>('/general-info/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Role Permissions service
export const rolePermissionsService = {
  async getByRoleId(roleId: number): Promise<Permission[]> {
    const response = await api.get(`/admin/roles/${roleId}/permissions`);
    return response.data;
  },
  async assign(roleId: number, permissionId: number): Promise<void> {
    await api.post(`/admin/roles/${roleId}/permissions/${permissionId}`);
  },
  async revoke(roleId: number, permissionId: number): Promise<void> {
    await api.delete(`/admin/roles/${roleId}/permissions/${permissionId}`);
  },
};

